import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Canvas } from "@react-three/fiber";
import { Environment, OrthographicCamera, Loader } from "@react-three/drei";
import Avatar from "./components/Avatar";
import "./App.css";

// Add this inline style object for basic layout
const styles = {
  container: {
    display: "flex",
    width: "100%",
    height: "100vh",
  },
  avatarSection: {
    width: "50%",
    height: "100%",
    position: "relative",
  },
  uiSection: {
    width: "50%",
    height: "100%",
    overflowY: "auto",
    backgroundColor: "#f0f1f7",
    padding: "20px",
  },
  card: {
    maxWidth: "100%",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "20px",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#6445b7",
    marginBottom: "16px",
  },
  timer: {
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#f5f5f5",
    padding: "8px 16px",
    borderRadius: "4px",
    marginBottom: "16px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginBottom: "16px",
  },
  button: {
    padding: "8px 16px",
    borderRadius: "4px",
    fontWeight: "bold",
    color: "white",
    cursor: "pointer",
    border: "none",
  },
  startButton: {
    backgroundColor: "#4cae4f",
  },
  stopButton: {
    backgroundColor: "#e74c3c",
  },
  submitButton: {
    backgroundColor: "#4cae4f",
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    marginTop: "20px",
  },
  disabledButton: {
    backgroundColor: "#cccccc",
    cursor: "not-allowed",
    opacity: 0.7,
  },
  listeningStatus: {
    textAlign: "center",
    fontWeight: "bold",
    color: "#e74c3c",
    marginBottom: "16px",
  },
  messageBox: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
    marginBottom: "16px",
  },
  subcategoryList: {
    marginTop: "20px",
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  radioItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  radioItemSelected: {
    backgroundColor: "#f0e6ff",
    borderColor: "#6445b7",
  },
  radioInput: {
    marginRight: "10px",
  },
  radioLabel: {
    fontWeight: "500",
  },
  audioControlContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  audioControlLabel: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    color: "#444",
    userSelect: "none",
  },
  audioDisabledIndicator: {
    marginLeft: "8px",
    fontSize: "14px",
    color: "#e74c3c",
    fontWeight: "bold",
  },
  audioEnabledIndicator: {
    marginLeft: "8px",
    fontSize: "14px",
    color: "#4cae4f",
    fontWeight: "bold",
  },
  statusText: {
    textAlign: "center",
    fontStyle: "italic",
    color: "#666",
    marginBottom: "8px",
  },
  topicDisplay: {
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#6445b7",
    margin: "16px 0",
  },
  questionText: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#444",
    marginBottom: "16px",
  },
  optionsList: {
    marginTop: "16px",
    marginBottom: "24px",
  },
  scoreText: {
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#6445b7",
    margin: "16px 0",
  },
  nextButton: {
    backgroundColor: "#3498db",
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    marginTop: "20px",
  },
  feedbackCorrect: {
    backgroundColor: "#d4edda",
    color: "#155724",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    padding: "12px",
    marginBottom: "16px",
  },
  feedbackIncorrect: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    border: "1px solid #f5c6cb",
    borderRadius: "8px",
    padding: "12px",
    marginBottom: "16px",
  },
};

function App() {
  // Avatar state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentText, setCurrentText] = useState("");

  // Quiz UI state
  const [timer, setTimer] = useState("00:00:00");
  const [isGameActive, setIsGameActive] = useState(false);
  const [listeningStatus, setListeningStatus] = useState("");
  const [status, setStatus] = useState("");

  // New quiz flow states
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [currentTopic, setCurrentTopic] = useState("");
  const [selectedQuizId, setSelectedQuizId] = useState("");
  const [gameStage, setGameStage] = useState("initial"); // initial, topic_selection, subcategory_selection, quiz

  // Quiz question states
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [questionResult, setQuestionResult] = useState(null);
  const [score, setScore] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);

  // Audio disable state
  const [isAudioDisabled, setIsAudioDisabled] = useState(false);
  const isAudioDisabledRef = useRef(false);

  // Refs
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingAudioRef = useRef(false);
  const isRecordingRef = useRef(false);
  const ucidRef = useRef(Date.now().toString());
  const packetCounterRef = useRef(0);
  const processorRef = useRef(null);
  const inputStreamRef = useRef(null);
  const globalStreamRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const secondsRef = useRef(0);
  const minutesRef = useRef(0);
  const hoursRef = useRef(0);
  const stopGameRef = useRef(null); // Ref to avoid circular dependencies
  const userIdRef = useRef(1); // Default userId value
  const gradeRef = useRef(1);
  const userNameRef = useRef("Child");
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("userId");
    const grade = urlParams.get("grade");
    const userName = urlParams.get("userName");
    userIdRef.current = userId;
    gradeRef.current = grade;
    userNameRef.current = userName;
  }, []);

  // Debug logging
  const debugLog = useCallback((message) => {
    const timestamp = new Date().toISOString();
    console.log(`${timestamp}: ${message}`);
  }, []);

  // Keep audio disabled ref in sync with state
  useEffect(() => {
    isAudioDisabledRef.current = isAudioDisabled;
    debugLog(`Audio disabled state changed: ${isAudioDisabled}`);

    // If audio is disabled, stop any currently playing audio and clear the queue
    if (isAudioDisabled) {
      // Stop currently playing audio
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop();
        } catch (err) {
          debugLog(`Error stopping audio source: ${err.message}`);
        }
        audioSourceRef.current = null;
      }

      // Clear audio queue
      audioQueueRef.current = [];
      isPlayingAudioRef.current = false;
      setIsSpeaking(false);
      debugLog("Stopped audio playback and cleared queue");
    }
  }, [isAudioDisabled, debugLog]);

  // Update status message
  const updateStatus = useCallback(
    (message) => {
      setStatus(message);
      debugLog(`Status update: ${message}`);
    },
    [debugLog]
  );

  // Timer functions
  const updateTimer = useCallback(() => {
    secondsRef.current++;
    if (secondsRef.current >= 60) {
      secondsRef.current = 0;
      minutesRef.current++;
      if (minutesRef.current >= 60) {
        minutesRef.current = 0;
        hoursRef.current++;
      }
    }
    setTimer(
      `${String(hoursRef.current).padStart(2, "0")}:${String(
        minutesRef.current
      ).padStart(2, "0")}:${String(secondsRef.current).padStart(2, "0")}`
    );
  }, []);

  const startTimer = useCallback(() => {
    timerIntervalRef.current = setInterval(updateTimer, 1000);
  }, [updateTimer]);

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    secondsRef.current = 0;
    minutesRef.current = 0;
    hoursRef.current = 0;
    setTimer("00:00:00");
  }, []);

  // Audio encoding function
  const encodePCM16 = useCallback((buffer) => {
    const length = buffer.length * 2;
    const buffer16 = new ArrayBuffer(length);
    const view = new DataView(buffer16);
    let offset = 0;
    for (let i = 0; i < buffer.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, buffer[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer16;
  }, []);

  // Recording controls
  const startRecording = useCallback(() => {
    isRecordingRef.current = true;
    setListeningStatus("Listening for your answer...");
    debugLog("Started recording");
  }, [debugLog]);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    setListeningStatus("");
    debugLog("Stopped recording");
  }, [debugLog]);

  // Stop game function - defined first to avoid reference issues
  const stopGame = useCallback(() => {
    debugLog("Stopping game...");

    if (isRecordingRef.current) {
      stopRecording();
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch (err) {
        debugLog(`Error disconnecting processor: ${err.message}`);
      }
      processorRef.current = null;
    }

    if (inputStreamRef.current) {
      try {
        inputStreamRef.current.disconnect();
      } catch (err) {
        debugLog(`Error disconnecting input stream: ${err.message}`);
      }
      inputStreamRef.current = null;
    }

    if (globalStreamRef.current) {
      try {
        globalStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch (err) {
        debugLog(`Error stopping tracks: ${err.message}`);
      }
      globalStreamRef.current = null;
    }

    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch (err) {
        debugLog(`Error stopping audio source: ${err.message}`);
      }
      audioSourceRef.current = null;
    }

    audioQueueRef.current = [];
    packetCounterRef.current = 0;
    isPlayingAudioRef.current = false;
    setIsSpeaking(false);

    setIsGameActive(false);
    setListeningStatus("");
    setGameStage("initial");
    setSubcategories([]);
    setSelectedSubcategory(null);
    setCurrentTopic("");
    setCurrentQuestion(null);
    setCurrentOptions([]);
    setSelectedOption(null);
    setSelectedOptionIndex(null);
    setQuestionResult(null);
    setScore(0);
    setTotalAttempts(0);
    setIsQuizCompleted(false);
    updateStatus("Game stopped");
    debugLog("Game stopped and cleaned up");
    stopTimer();
  }, [debugLog, stopRecording, stopTimer, updateStatus]);

  // Store stopGame in ref to avoid circular dependencies
  useEffect(() => {
    stopGameRef.current = stopGame;
  }, [stopGame]);

  // Audio playback function
  const playNextInQueue = useCallback(async () => {
    // Don't play audio if audio is disabled
    if (isAudioDisabledRef.current) {
      debugLog("Audio playback skipped - audio is disabled");
      audioQueueRef.current = []; // Clear queue when disabled
      return;
    }

    if (audioQueueRef.current.length === 0 || isPlayingAudioRef.current) {
      return;
    }

    isPlayingAudioRef.current = true;
    const audioData = audioQueueRef.current.shift();

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }

      const audioBuffer = await audioContextRef.current.decodeAudioData(
        audioData
      );
      const source = audioContextRef.current.createBufferSource();

      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      audioSourceRef.current = source;

      source.onended = () => {
        debugLog("Audio playback completed");
        audioSourceRef.current = null;
        isPlayingAudioRef.current = false;
        setIsSpeaking(false);

        // Notify server of completion if needed
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "audio_completed" }));
        }

        // Start recording after a short delay
        setTimeout(() => {
          if (
            !isRecordingRef.current &&
            isGameActive &&
            gameStage === "topic_selection"
          ) {
            startRecording();
          }

          // Play next audio if any (only if audio is not disabled)
          if (audioQueueRef.current.length > 0 && !isAudioDisabledRef.current) {
            playNextInQueue();
          }
        }, 200);
      };

      debugLog("Starting audio playback");
      setIsSpeaking(true);
      source.start(0);
    } catch (error) {
      debugLog(`Error processing audio: ${error.message}`);
      audioSourceRef.current = null;
      isPlayingAudioRef.current = false;
      if (audioQueueRef.current.length > 0 && !isAudioDisabledRef.current) {
        playNextInQueue();
      }
    }
  }, [debugLog, isGameActive, gameStage, startRecording]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback(
    (data) => {
      debugLog(`Processing message type: ${data.type}`);

      switch (data.type) {
        case "subcategories":
          setGameStage("subcategory_selection");
          setCurrentTopic(data.topic);
          setSubcategories(data.subcategories);
          break;

        case "game_question":
          setGameStage("quiz");
          setCurrentQuestion(data.question);
          setCurrentOptions(data.options);
          setSelectedOption(null);
          setSelectedOptionIndex(null);
          setQuestionResult(null);
          break;

        case "game_update":
          setQuestionResult({
            isCorrect: data.is_correct,
            correctOption: data.correct_option,
            selectedOption: data.selected_option,
          });
          setScore(data.score);
          setTotalAttempts(data.attempts);
          break;

        case "game_over":
          setIsQuizCompleted(true);
          setGameStage("results");
          setScore(data.score);
          setTotalAttempts(data.attempts);
          break;

        case "text_chunk":
          setCurrentText(data.text);
          break;

        case "error":
          updateStatus(data.message || "An error occurred");
          break;

        default:
          break;
      }
    },
    [debugLog, updateStatus]
  );

  // Initialize audio capture
  const initializeAudioCapture = useCallback(async () => {
    try {
      debugLog("Initializing audio capture...");
      const sampleRate = 16000;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      globalStreamRef.current = stream;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: sampleRate,
        });
      }

      inputStreamRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      processorRef.current = audioContextRef.current.createScriptProcessor(
        4096,
        1,
        1
      );

      processorRef.current.onaudioprocess = (e) => {
        // Check if audio is disabled - if so, don't send any audio data
        if (isAudioDisabledRef.current) {
          return; // Skip processing when audio is disabled
        }

        if (
          isRecordingRef.current &&
          wsRef.current &&
          wsRef.current.readyState === WebSocket.OPEN
        ) {
          const inputData = e.inputBuffer.getChannelData(0);
          const audioData = encodePCM16(inputData);

          packetCounterRef.current++;
          const message = {
            type: "media",
            event: "media",
            ucid: ucidRef.current,
            data: {
              samples: Array.from(new Int16Array(audioData)),
              bitsPerSample: 16,
              sampleRate: sampleRate,
              channelCount: 1,
              numberOfFrames: inputData.length,
              type: "data",
            },
          };

          try {
            wsRef.current.send(JSON.stringify(message));
            debugLog(`Sent audio packet #${packetCounterRef.current}`);
          } catch (error) {
            debugLog(`Error sending audio: ${error.message}`);
          }
        }
      };

      inputStreamRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);
      debugLog("Audio capture initialized successfully");
      return true;
    } catch (error) {
      debugLog(`Error initializing audio: ${error.message}`);
      updateStatus("Microphone access denied or not available");
      return false;
    }
  }, [debugLog, encodePCM16, updateStatus]);

  // WebSocket setup
  const setupWebSocket = useCallback(() => {
    const wsUrl = `wss://quiz.vplaysmart.com/ws`;
    debugLog("Setting up WebSocket connection to: " + wsUrl);

    if (wsRef.current) {
      wsRef.current.close();
    }

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      debugLog("WebSocket Connected");
      updateStatus("Connected! Starting game...");

      wsRef.current.send(
        JSON.stringify({
          event: "start",
          type: "start",
          ucid: ucidRef.current,
          userId: userIdRef.current,
          grade: gradeRef.current,
          userName: userNameRef.current,
        })
      );

      // Start the game once connected
      setIsGameActive(true);
      setGameStage("topic_selection");
      startTimer();
      startRecording();
    };

    wsRef.current.onmessage = async (event) => {
      try {
        debugLog(`Received message type: ${typeof event.data}`);

        if (event.data instanceof Blob) {
          // Don't process audio blobs if audio is disabled
          if (isAudioDisabledRef.current) {
            debugLog("Audio blob received but ignored - audio is disabled");
            return;
          }
          debugLog("Received audio blob");
          const arrayBuffer = await event.data.arrayBuffer();
          audioQueueRef.current.push(arrayBuffer);
          if (!isPlayingAudioRef.current) {
            playNextInQueue();
          }
        } else {
          const message = JSON.parse(event.data);
          debugLog(`Received JSON message: ${JSON.stringify(message)}`);
          handleWebSocketMessage(message);
        }
      } catch (error) {
        debugLog(`Error processing message: ${error.message}`);
      }
    };

    wsRef.current.onerror = (error) => {
      debugLog(`WebSocket error: ${error}`);
      updateStatus("Connection error occurred");
      if (stopGameRef.current) {
        stopGameRef.current();
      }
    };

    wsRef.current.onclose = () => {
      debugLog("WebSocket Disconnected");
      updateStatus("Connection closed");
    };

    return wsRef.current;
  }, [
    debugLog,
    handleWebSocketMessage,
    playNextInQueue,
    startRecording,
    startTimer,
    updateStatus,
  ]);

  // Start game function
  const startGame = useCallback(async () => {
    try {
      updateStatus("Initializing audio...");

      const audioInitialized = await initializeAudioCapture();
      if (!audioInitialized) return;

      // Reset game state
      setCurrentText("");
      setGameStage("topic_selection");
      setCurrentQuestion(null);
      setCurrentOptions([]);
      setSelectedOption(null);
      setSelectedOptionIndex(null);
      setQuestionResult(null);
      setScore(0);
      setTotalAttempts(0);
      setIsQuizCompleted(false);

      updateStatus("Connecting to game server...");
      setupWebSocket();
    } catch (error) {
      debugLog(`Error in startGame: ${error.message}`);
      updateStatus("Failed to start game");
      if (stopGameRef.current) {
        stopGameRef.current();
      }
    }
  }, [debugLog, initializeAudioCapture, setupWebSocket, updateStatus]);

  // Handle subcategory selection
  const handleSubcategorySelection = useCallback((subcategoryId) => {
    setSelectedSubcategory(subcategoryId);
  }, []);

  // Submit selected subcategory
  const submitSubcategorySelection = useCallback(() => {
    if (
      !selectedSubcategory ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    ) {
      return;
    }

    debugLog(`Submitting subcategory selection: ${selectedSubcategory}`);

    // Disable the microphone when submitting subcategory
    wsRef.current.send(
      JSON.stringify({
        type: "toggle_mic",
        enabled: false,
      })
    );

    wsRef.current.send(
      JSON.stringify({
        type: "subcategory_selected",
        subcategory_id: selectedSubcategory,
      })
    );

    updateStatus("Submitting your selection...");
  }, [selectedSubcategory, debugLog, updateStatus]);

  // Handle option selection
  const handleOptionSelection = useCallback((option, index) => {
    setSelectedOption(option);
    setSelectedOptionIndex(index);
  }, []);

  // Submit selected answer
  const submitAnswer = useCallback(() => {
    if (
      selectedOption === null ||
      selectedOptionIndex === null ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    ) {
      return;
    }

    debugLog(
      `Submitting answer: ${selectedOption}, index: ${selectedOptionIndex}`
    );

    wsRef.current.send(
      JSON.stringify({
        type: "answer",
        answer: selectedOption,
        option_index: selectedOptionIndex,
      })
    );

    updateStatus("Submitting your answer...");
  }, [selectedOption, selectedOptionIndex, debugLog, updateStatus]);

  // Move to next question
  const moveToNextQuestion = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    debugLog("Requesting next question");

    wsRef.current.send(
      JSON.stringify({
        type: "next_question",
      })
    );

    updateStatus("Loading next question...");
  }, [debugLog, updateStatus]);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (stopGameRef.current) {
        stopGameRef.current();
      }
    };
  }, []);

  // Render different UI based on game stage
  const renderGameContent = () => {
    switch (gameStage) {
      case "initial":
        return (
          <>
            <div style={styles.buttonContainer}>
              <button
                onClick={startGame}
                disabled={isGameActive}
                style={{
                  ...styles.button,
                  ...(isGameActive
                    ? styles.disabledButton
                    : styles.startButton),
                }}
              >
                START
              </button>
              <button
                onClick={stopGame}
                disabled={!isGameActive}
                style={{
                  ...styles.button,
                  ...(!isGameActive
                    ? styles.disabledButton
                    : styles.stopButton),
                }}
              >
                STOP
              </button>
            </div>

            <div style={styles.messageBox}>
              <p>Welcome! Click START to begin the knowledge quiz.</p>
            </div>
          </>
        );

      case "topic_selection":
        return (
          <>
            <div style={styles.buttonContainer}>
              <button
                onClick={stopGame}
                style={{
                  ...styles.button,
                  ...styles.stopButton,
                }}
              >
                STOP
              </button>
            </div>

            {listeningStatus && (
              <div style={styles.listeningStatus}>{listeningStatus}</div>
            )}

            <div style={styles.messageBox}>
              <p>What topic have you learned today? Please say it clearly.</p>
              {currentText && (
                <p>
                  <strong>Last heard: </strong>
                  {currentText}
                </p>
              )}
            </div>
          </>
        );

      case "subcategory_selection":
        return (
          <>
            <div style={styles.buttonContainer}>
              <button
                onClick={stopGame}
                style={{
                  ...styles.button,
                  ...styles.stopButton,
                }}
              >
                STOP
              </button>
            </div>

            <div style={styles.topicDisplay}>Topic: {currentTopic}</div>

            <div style={styles.messageBox}>
              <p>Please select a subcategory to continue:</p>

              <div style={styles.subcategoryList}>
                <div style={styles.radioGroup}>
                  {subcategories.map((subcategory) => (
                    <div
                      key={subcategory.id}
                      style={{
                        ...styles.radioItem,
                        ...(selectedSubcategory === subcategory.id
                          ? styles.radioItemSelected
                          : {}),
                      }}
                      onClick={() => handleSubcategorySelection(subcategory.id)}
                    >
                      <input
                        type="radio"
                        id={`subcategory-${subcategory.id}`}
                        name="subcategory"
                        value={subcategory.id}
                        checked={selectedSubcategory === subcategory.id}
                        onChange={() =>
                          handleSubcategorySelection(subcategory.id)
                        }
                        style={styles.radioInput}
                      />
                      <label
                        htmlFor={`subcategory-${subcategory.id}`}
                        style={styles.radioLabel}
                      >
                        {subcategory.sub_category}
                      </label>
                    </div>
                  ))}
                </div>

                <button
                  onClick={submitSubcategorySelection}
                  disabled={!selectedSubcategory}
                  style={{
                    ...styles.button,
                    ...styles.submitButton,
                    ...(!selectedSubcategory ? styles.disabledButton : {}),
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        );

      case "quiz":
        return (
          <>
            <div style={styles.buttonContainer}>
              <button
                onClick={stopGame}
                style={{
                  ...styles.button,
                  ...styles.stopButton,
                }}
              >
                STOP
              </button>
            </div>

            <div style={styles.scoreText}>
              Score: {score}/{totalAttempts}
            </div>

            <div style={styles.messageBox}>
              {currentQuestion && (
                <div>
                  <div style={styles.questionText}>{currentQuestion}</div>

                  {questionResult ? (
                    <div
                      style={
                        questionResult.isCorrect
                          ? styles.feedbackCorrect
                          : styles.feedbackIncorrect
                      }
                    >
                      {questionResult.isCorrect
                        ? "Correct! Well done."
                        : `Incorrect. The correct answer is: ${questionResult.correctOption}`}
                    </div>
                  ) : null}

                  <div style={styles.optionsList}>
                    <div style={styles.radioGroup}>
                      {currentOptions.map((option, index) => (
                        <div
                          key={index}
                          style={{
                            ...styles.radioItem,
                            ...(selectedOptionIndex === index
                              ? styles.radioItemSelected
                              : {}),
                          }}
                          onClick={() =>
                            !questionResult &&
                            handleOptionSelection(option, index)
                          }
                        >
                          <input
                            type="radio"
                            id={`option-${index}`}
                            name="quiz-option"
                            value={option}
                            checked={selectedOptionIndex === index}
                            onChange={() =>
                              !questionResult &&
                              handleOptionSelection(option, index)
                            }
                            disabled={!!questionResult}
                            style={styles.radioInput}
                          />
                          <label
                            htmlFor={`option-${index}`}
                            style={styles.radioLabel}
                          >
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {!questionResult ? (
                    <button
                      onClick={submitAnswer}
                      disabled={selectedOption === null}
                      style={{
                        ...styles.button,
                        ...styles.submitButton,
                        ...(selectedOption === null
                          ? styles.disabledButton
                          : {}),
                      }}
                    >
                      Submit Answer
                    </button>
                  ) : (
                    <button
                      onClick={moveToNextQuestion}
                      style={{
                        ...styles.button,
                        ...styles.nextButton,
                      }}
                    >
                      Next Question
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        );

      case "results":
        return (
          <>
            <div style={styles.buttonContainer}>
              <button
                onClick={stopGame}
                style={{
                  ...styles.button,
                  ...styles.stopButton,
                }}
              >
                STOP
              </button>
            </div>

            <div style={styles.messageBox}>
              <h2 style={styles.heading}>Quiz Completed!</h2>
              <div style={styles.scoreText}>
                Final Score: {score}/{totalAttempts}
              </div>
              <p style={{ textAlign: "center", marginTop: "16px" }}>
                {score / totalAttempts >= 0.7 ? (
                  "Great job! You've mastered this topic!"
                ) : (
                  <>
                    <a
                      href="https://vplaysmart.com/course/sciencebee-grade5/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Keep practicing to improve your knowledge!
                    </a>
                  </>
                )}
              </p>

              <button
                onClick={startGame}
                style={{
                  ...styles.button,
                  ...styles.startButton,
                  width: "100%",
                  marginTop: "20px",
                }}
              >
                Start New Quiz
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app-container" style={styles.container}>
      {/* 3D Avatar Section - Left Side */}
      <div className="avatar-section" style={styles.avatarSection}>
        <Canvas>
          <OrthographicCamera makeDefault zoom={1000} position={[0, 1.55, 1]} />
          <Suspense fallback={null}>
            <Environment
              background={false}
              files="/images/photo_studio_loft_hall_1k.hdr"
            />
          </Suspense>
          <Suspense fallback={null}>
            <Avatar
              avatar_url="/model15.glb"
              isSpeaking={isSpeaking}
              currentText={currentText}
              audioContext={audioContextRef.current}
              audioSource={audioSourceRef.current}
            />
          </Suspense>
        </Canvas>
        <Loader dataInterpolation={(p) => `Loading... please wait`} />
      </div>

      {/* Quiz UI - Right Side */}
      <div className="ui-section" style={styles.uiSection}>
        <div style={styles.card}>
          <h1 style={styles.heading}>Knowledge Quiz</h1>

          {/* Audio Disable Checkbox */}
          <div style={styles.audioControlContainer}>
            <label style={styles.audioControlLabel}>
              <input
                type="checkbox"
                checked={isAudioDisabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsAudioDisabled(checked);
                  isAudioDisabledRef.current = checked;
                  debugLog(`Audio ${checked ? "disabled" : "enabled"} by user`);
                  updateStatus(
                    checked
                      ? "Audio disabled - Avatar will stop streaming"
                      : "Audio enabled - Avatar will resume streaming"
                  );
                }}
                style={styles.checkbox}
              />
              Disable Audio
              {isAudioDisabled ? (
                <span style={styles.audioDisabledIndicator}>
                  (Audio OFF - Avatar stopped)
                </span>
              ) : (
                <span style={styles.audioEnabledIndicator}>
                  (Audio ON - Avatar active)
                </span>
              )}
            </label>
          </div>

          <div style={styles.timer}>{timer}</div>

          {renderGameContent()}

          {status && <div style={styles.statusText}>{status}</div>}
        </div>
      </div>
    </div>
  );
}

export default App;
